// 26 ADM-041 and 07 CRS-037: the course review queue (decision record 0287). Owner only, a reason for every
// action. One read, ops.admin_course_queue(): the drafts whose job is in review, with the pipeline's checks,
// its name and copy checks, its figures and each tee set's first rating. The actions are the commands
// courses.approve and courses.reject (07 CRS-032), which check everything again on the server: approval
// needs the picture check's pass or the owner's word that they looked at every hole (the check is not
// built yet), and a flagship or partner course needs the founder's sign-off (CRS-012).
// Below the queue, the courses in play (07 CRS-038; decision record 0308): make one again on a new seed
// (courses.regenerate), or take it out of play (courses.pull), each with a reason.
import type { SupabaseClient } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { adminLength, adminText } from '../lib/copy';
import { messageFor, runCommand, type CommandResult } from '../lib/commands';

export interface QueueItem {
  course_id: string;
  course_version_id: string;
  version: number;
  name: string | null;
  style: string;
  is_flagship: boolean;
  licence_type: string;
  area_code: string | null;
  site_osm_id: string | null;
  seed: string | null;
  vision_check_result: string | null;
  name_check: { passes?: boolean; hits?: { entry: string; reason: string }[] } | null;
  similarity: { hole_no: number; real_ref: string; rejected: boolean }[] | null;
  checks: {
    valid?: boolean;
    design?: { problems?: { rule: string; hole_no: number | null }[] };
    schema?: unknown[];
  } | null;
  figures: {
    bunker_count?: number;
    water_holes?: number;
    tree_count?: number;
    green_area_m2?: number;
  } | null;
  tee_sets: { name: string; length_m: number; par: number }[] | null;
  ratings:
    | { tee_set: string; scratch_score: number; bogey_score: number; difficulty_factor: number }[]
    | null;
}

export async function loadQueue(sb: SupabaseClient): Promise<QueueItem[] | null> {
  const { data, error } = await sb.schema('ops').rpc('admin_course_queue');
  if (error) return null;
  return (data ?? []) as QueueItem[];
}

/** The automatic checks that failed, in plain words, hole by hole (25 TON-068: never a code on its own). */
const whole = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
const tenth = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const num = (v: number | undefined, f: Intl.NumberFormat) => (v === undefined ? '-' : f.format(v));

export function problemLines(item: QueueItem): string[] {
  const problems = item.checks?.design?.problems ?? [];
  const lines = problems.map((p) => {
    const message = adminText(`course_check.${p.rule}`);
    return p.hole_no === null
      ? adminText('courses.whole_course', { message })
      : adminText('courses.on_hole', { hole: p.hole_no, message });
  });
  if ((item.checks?.schema ?? []).length > 0)
    lines.unshift(adminText('courses.whole_course', { message: adminText('course_check.schema') }));
  return lines;
}

function CourseCard({
  item,
  sb,
  onDone,
}: {
  item: QueueItem;
  sb: SupabaseClient;
  onDone: (text: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [looked, setLooked] = useState(false);
  const [founder, setFounder] = useState(false);
  const [msg, setMsg] = useState('');
  const problems = problemLines(item);
  const needsFounder = item.is_flagship || item.licence_type === 'partner_free';
  const hits = item.name_check?.hits ?? [];
  const copies = new Set((item.similarity ?? []).filter((s) => s.rejected).map((s) => s.hole_no))
    .size;
  const id = item.course_version_id;
  const needs = [
    problems.length > 0 && adminText('courses.needs_checks'),
    !reason.trim() && adminText('courses.needs_reason'),
    item.vision_check_result !== 'pass' && !looked && adminText('courses.needs_looked'),
    needsFounder && !founder && adminText('courses.needs_founder'),
  ].filter((x): x is string => Boolean(x));
  const act = async (command: string, args: Record<string, unknown>, done: string) => {
    if (!reason.trim()) return setMsg(adminText('courses.reason_label'));
    const r: CommandResult = await runCommand(
      sb,
      command,
      { course_version_id: id, ...args },
      reason.trim(),
    );
    if (r.ok) onDone(done);
    else setMsg(messageFor(r));
  };
  return (
    <article className="card" aria-labelledby={`c-${id}`}>
      <div className="title-row">
        <h3 id={`c-${id}`}>{item.name ?? item.course_id}</h3>
        <span className="chip">{adminText('courses.style', { style: item.style })}</span>
      </div>
      <p className="muted wrap">
        {adminText('courses.site', { site: item.site_osm_id ?? '-', seed: item.seed ?? '-' })}
      </p>
      {item.is_flagship && <p className="warn">{adminText('courses.flagship')}</p>}
      {!item.is_flagship && item.licence_type === 'partner_free' && (
        <p className="warn">{adminText('courses.partner')}</p>
      )}
      {problems.length === 0 ? (
        <p>{adminText('courses.checks_ok')}</p>
      ) : (
        <>
          <p className="warn">{adminText('courses.checks_failed', { n: problems.length })}</p>
          <ul>
            {problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </>
      )}
      <p>
        {hits.length === 0
          ? adminText('courses.name_ok')
          : adminText('courses.name_hits', {
              n: hits.length,
              names: hits.map((h) => h.entry).join(', '),
            })}
      </p>
      <p>
        {copies === 0
          ? adminText('courses.copy_ok')
          : adminText('courses.copy_hits', { n: copies })}
      </p>
      {item.figures && (
        <p className="muted">
          {adminText('courses.figures', {
            bunkers: whole.format(item.figures.bunker_count ?? 0),
            water: whole.format(item.figures.water_holes ?? 0),
            trees: whole.format(item.figures.tree_count ?? 0),
            green_m2: whole.format(item.figures.green_area_m2 ?? 0),
          })}
        </p>
      )}
      <h4>{adminText('courses.tees_heading')}</h4>
      <ul>
        {(item.tee_sets ?? []).map((t) => {
          const r = (item.ratings ?? []).find((x) => x.tee_set === t.name);
          return (
            <li key={t.name}>
              <strong>
                {adminText('courses.tee_line', {
                  name: t.name,
                  length: adminLength(t.length_m),
                  par: t.par,
                })}
              </strong>
              <br />
              <span className="muted">
                {adminText('courses.tee_rating', {
                  scratch: num(r?.scratch_score, tenth),
                  bogey: num(r?.bogey_score, tenth),
                  df: num(r?.difficulty_factor, whole),
                })}
              </span>
            </li>
          );
        })}
      </ul>
      {item.vision_check_result !== 'pass' && (
        <p className="note">{adminText('courses.picture_check')}</p>
      )}
      <label htmlFor={`r-${id}`}>{adminText('courses.reason_label')}</label>
      <input id={`r-${id}`} value={reason} onChange={(e) => setReason(e.target.value)} />
      {item.vision_check_result !== 'pass' && (
        <label className="check">
          <input type="checkbox" checked={looked} onChange={(e) => setLooked(e.target.checked)} />
          {adminText('courses.looked')}
        </label>
      )}
      {needsFounder && (
        <label className="check">
          <input type="checkbox" checked={founder} onChange={(e) => setFounder(e.target.checked)} />
          {adminText('courses.founder')}
        </label>
      )}
      {needs.length > 0 && (
        <div className="muted" id={`n-${id}`}>
          <p>{adminText('courses.needs_heading')}</p>
          <ul>
            {needs.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="actions">
        <button
          disabled={needs.length > 0}
          aria-describedby={needs.length > 0 ? `n-${id}` : undefined}
          onClick={() =>
            void act(
              'courses.approve',
              { vision_override: item.vision_check_result !== 'pass', founder_sign_off: founder },
              adminText('courses.done_approve'),
            )
          }
        >
          {adminText('courses.approve')}
        </button>
        <button onClick={() => void act('courses.reject', {}, adminText('courses.done_reject'))}>
          {adminText('courses.reject')}
        </button>
        <button
          onClick={() =>
            void act('courses.reject', { regenerate: true }, adminText('courses.done_remake'))
          }
        >
          {adminText('courses.remake')}
        </button>
      </div>
      {msg && (
        <p role="status" className="warn">
          {msg}
        </p>
      )}
    </article>
  );
}

export interface InPlayItem {
  course_id: string;
  name: string;
  style: string;
  area_code: string | null;
  status: string;
  is_flagship: boolean;
  version: number | null;
  job_waiting: boolean;
}

export async function loadInPlay(sb: SupabaseClient): Promise<InPlayItem[] | null> {
  const { data, error } = await sb.schema('ops').rpc('admin_courses_in_play');
  if (error || !Array.isArray(data)) return null;
  return data as InPlayItem[];
}

function InPlayRow({
  item,
  sb,
  onDone,
}: {
  item: InPlayItem;
  sb: SupabaseClient;
  onDone: (text: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState('');
  const id = item.course_id;
  const act = async (command: string, done: string) => {
    if (!reason.trim()) return setMsg(adminText('courses.needs_reason'));
    const r: CommandResult = await runCommand(sb, command, { course_id: id }, reason.trim());
    if (r.ok) onDone(done);
    else setMsg(messageFor(r));
  };
  return (
    <li className="card" aria-labelledby={`p-${id}`}>
      <div className="title-row">
        <h4 id={`p-${id}`}>{item.name}</h4>
        <span className="chip">
          {adminText('in_play.line', { style: item.style, version: item.version ?? '-' })}
        </span>
      </div>
      {item.job_waiting && (
        <p className="muted" id={`pw-${id}`}>
          {adminText('in_play.waiting')}
        </p>
      )}
      <label htmlFor={`pr-${id}`}>{adminText('in_play.reason_label', { name: item.name })}</label>
      <input id={`pr-${id}`} value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="actions">
        <button
          disabled={item.job_waiting}
          aria-describedby={item.job_waiting ? `pw-${id}` : undefined}
          onClick={() =>
            void act(
              'courses.regenerate',
              adminText('in_play.done_regenerate', { name: item.name }),
            )
          }
        >
          {adminText('in_play.regenerate')}
        </button>
        <button
          className="primary danger"
          onClick={() => {
            if (window.confirm(adminText('in_play.pull_confirm', { name: item.name })))
              void act('courses.pull', adminText('in_play.done_pull', { name: item.name }));
          }}
        >
          {adminText('in_play.pull')}
        </button>
      </div>
      {msg && (
        <p role="status" className="warn">
          {msg}
        </p>
      )}
    </li>
  );
}

export function InPlaySection({ sb }: { sb: SupabaseClient }) {
  const [items, setItems] = useState<InPlayItem[] | null | undefined>(undefined);
  const [done, setDone] = useState('');
  const load = () => {
    void loadInPlay(sb).then(setItems);
  };
  useEffect(load, [sb]);
  return (
    <section aria-labelledby="in-play-h">
      <h3 id="in-play-h">{adminText('in_play.heading')}</h3>
      <p className="muted">{adminText('in_play.intro')}</p>
      {done && <p role="status">{done}</p>}
      {items === undefined && (
        <p className="muted" role="status">
          {adminText('in_play.loading')}
        </p>
      )}
      {items === null && (
        <>
          <p className="warn" role="alert">
            {adminText('in_play.error')}
          </p>
          <p>
            <button type="button" onClick={load}>
              {adminText('config.try_again')}
            </button>
          </p>
        </>
      )}
      {items && items.length === 0 && <p>{adminText('in_play.empty')}</p>}
      {items && items.length > 0 && (
        <ul className="plain">
          {items.map((item) => (
            <InPlayRow
              key={item.course_id}
              item={item}
              sb={sb}
              onDone={(text) => {
                setDone(text);
                load();
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export function CoursesPage({ sb }: { sb: SupabaseClient }) {
  const [items, setItems] = useState<QueueItem[] | null | undefined>(undefined);
  const [done, setDone] = useState('');
  const load = () => {
    void loadQueue(sb).then(setItems);
  };
  useEffect(load, [sb]);
  return (
    <section aria-labelledby="courses-h">
      <h2 id="courses-h">{adminText('courses.heading')}</h2>
      <p className="muted">{adminText('courses.intro')}</p>
      {done && <p role="status">{done}</p>}
      {items === undefined && (
        <p className="muted" role="status">
          {adminText('courses.loading')}
        </p>
      )}
      {items === null && (
        <>
          <p className="warn" role="alert">
            {adminText('courses.error')}
          </p>
          <p>
            <button type="button" onClick={load}>
              {adminText('config.try_again')}
            </button>
          </p>
        </>
      )}
      {items && items.length === 0 && <p>{adminText('courses.empty')}</p>}
      {items && items.length > 0 && (
        <>
          <p>{adminText('courses.count', { n: items.length })}</p>
          {items.map((item) => (
            <CourseCard
              key={item.course_version_id}
              item={item}
              sb={sb}
              onDone={(text) => {
                setDone(text);
                load();
              }}
            />
          ))}
        </>
      )}
      <InPlaySection sb={sb} />
    </section>
  );
}
